import React, { useState, useEffect, useCallback } from 'react';
import { Loader2, RefreshCw, Send, CheckCircle2, Clock, Store } from 'lucide-react';

var API = process.env.REACT_APP_BACKEND_URL;

function headers() {
  var t = localStorage.getItem('admin_token');
  return t ? { Authorization: 'Bearer ' + t } : {};
}

// What has been pre-sold, who is waiting, and what to index next.
export default function PresalePanel() {
  var [rows, setRows] = useState([]);
  var [loading, setLoading] = useState(true);
  var [busy, setBusy] = useState('');
  var [msg, setMsg] = useState(null);

  var load = useCallback(async function() {
    setLoading(true);
    try {
      var r = await fetch(API + '/api/manual-orders/presales', { headers: headers() });
      var d = await r.json().catch(function() { return {}; });
      setRows(d.presales || []);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(function() { load(); }, [load]);

  async function toggleShop(slug, next) {
    setBusy(slug); setMsg(null);
    try {
      var r = await fetch(API + '/api/admin/product-presale/' + encodeURIComponent(slug), {
        method: 'PUT',
        headers: Object.assign({ 'Content-Type': 'application/json' }, headers()),
        body: JSON.stringify({ presale: next }),
      });
      if (!r.ok) throw new Error(r.status === 401
        ? 'Your admin session expired — log out and log in again, then try once more.'
        : 'Could not change that (HTTP ' + r.status + ')');
      await load();
      setMsg({ type: 'success', text: next ? 'Listed in the shop as a pre-order (and made visible)' : 'Pre-order label removed — still visible in the shop' });
    } catch (e) {
      setMsg({ type: 'error', text: e.message || String(e) });
    } finally {
      setBusy('');
    }
  }

  async function deliverNow(slug) {
    setBusy(slug); setMsg(null);
    try {
      var r = await fetch(API + '/api/manual-orders/presales/' + encodeURIComponent(slug) + '/deliver', {
        method: 'POST', headers: headers(),
      });
      var d = await r.json().catch(function() { return {}; });
      if (!r.ok) throw new Error(d.detail || 'Could not send the links');
      await load();
      setMsg({ type: 'success', text: 'Links sent to ' + (d.delivered || []).length + ' customer(s)' });
    } catch (e) {
      setMsg({ type: 'error', text: e.message || String(e) });
    } finally {
      setBusy('');
    }
  }

  var totalWaiting = rows.reduce(function(s, r) { return s + r.waiting_count; }, 0);

  return React.createElement('div', { className: 'bg-neutral-900 border border-white/10 rounded-2xl p-5', 'data-testid': 'presale-panel' },
    React.createElement('div', { className: 'flex flex-wrap items-center justify-between gap-3 mb-1' },
      React.createElement('h2', { className: 'text-white text-lg font-semibold' }, 'Pre-orders'),
      React.createElement('button', {
        type: 'button', onClick: load, 'data-testid': 'presale-refresh',
        className: 'flex items-center gap-1.5 text-xs text-neutral-300 border border-white/15 hover:border-white/40 rounded-full px-3 py-1.5'
      }, React.createElement(RefreshCw, { className: 'w-3.5 h-3.5' }), 'Refresh')
    ),
    React.createElement('p', { className: 'text-neutral-500 text-sm mb-5' },
      totalWaiting
        ? totalWaiting + ' customer' + (totalWaiting === 1 ? '' : 's') + ' waiting. Map the Dropbox file in Digital Files and everyone is emailed automatically.'
        : 'Nobody is waiting on a file right now. Mark a product as a pre-order to sell it before it is indexed.'),

    loading ? React.createElement('p', { className: 'py-8 text-center text-neutral-500 text-sm' },
      React.createElement(Loader2, { className: 'w-4 h-4 animate-spin inline mr-2' }), 'Loading…'
    ) : rows.length ? React.createElement('div', { className: 'space-y-3', 'data-testid': 'presale-rows' },
      rows.map(function(r) {
        return React.createElement('div', {
          key: r.slug, 'data-testid': 'presale-row',
          className: 'bg-black border border-white/10 rounded-xl px-4 py-3'
        },
          React.createElement('div', { className: 'flex flex-wrap items-center gap-3' },
            React.createElement('span', { className: 'flex-1 min-w-[180px] text-sm text-neutral-100 truncate' }, r.name),
            r.waiting_count ? React.createElement('span', {
              className: 'flex items-center gap-1.5 text-xs text-amber-300', 'data-testid': 'presale-waiting'
            }, React.createElement(Clock, { className: 'w-3.5 h-3.5' }),
              r.waiting_count + ' waiting') : React.createElement('span', { className: 'text-xs text-neutral-600' }, 'none waiting'),
            r.file_ready ? React.createElement('span', { className: 'flex items-center gap-1.5 text-xs text-emerald-400' },
              React.createElement(CheckCircle2, { className: 'w-3.5 h-3.5' }), 'file ready')
              : React.createElement('span', { className: 'text-xs text-neutral-500' }, 'no file yet'),
            React.createElement('button', {
              type: 'button', onClick: function() { toggleShop(r.slug, !r.in_shop_as_preorder); },
              disabled: busy === r.slug, 'data-testid': 'presale-toggle-shop',
              className: 'flex items-center gap-1.5 text-xs rounded-full px-3 py-1.5 border transition-colors ' +
                (r.in_shop_as_preorder
                  ? 'bg-[#d4af37] text-black border-[#d4af37] font-semibold'
                  : 'text-neutral-300 border-white/15 hover:border-white/40')
            }, React.createElement(Store, { className: 'w-3.5 h-3.5' }),
              r.in_shop_as_preorder ? 'In shop as pre-order' : 'Sell in shop'),
            (r.file_ready && r.waiting_count) ? React.createElement('button', {
              type: 'button', onClick: function() { deliverNow(r.slug); }, disabled: busy === r.slug,
              'data-testid': 'presale-deliver',
              className: 'flex items-center gap-1.5 text-xs bg-emerald-500/15 text-emerald-300 border border-emerald-500/40 hover:bg-emerald-500/25 rounded-full px-3 py-1.5'
            }, React.createElement(Send, { className: 'w-3.5 h-3.5' }),
              busy === r.slug ? 'Sending…' : 'Send links now') : null
          ),
          r.waiting_count ? React.createElement('p', { className: 'text-neutral-600 text-xs mt-2 truncate' },
            r.waiting.map(function(w) { return w.email; }).join(', ')) : null,
          r.hidden ? React.createElement('p', { className: 'text-neutral-600 text-xs mt-1' },
            'Hidden from the shop — only you can add it to an order') : null
        );
      })
    ) : React.createElement('p', { className: 'py-8 text-center text-neutral-600 text-sm' }, 'No pre-orders yet.'),

    msg ? React.createElement('div', {
      'data-testid': 'presale-message',
      className: 'mt-4 px-4 py-2 rounded-lg text-sm ' + (msg.type === 'success'
        ? 'bg-green-500/10 border border-green-500/30 text-green-300'
        : 'bg-red-500/10 border border-red-500/30 text-red-300')
    }, (msg.type === 'success' ? '✅ ' : '⚠️ ') + msg.text) : null
  );
}
